import React from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  KeyboardArrowDown as SortDescIcon,
  KeyboardArrowUp as SortAscIcon,
  OpenInNew as OpenInNewIcon,
  Save as SaveIcon,
  UnfoldMore as SortNeutralIcon,
  ViewColumn as ColumnsIcon
} from '@mui/icons-material';
import ArticleImage from '../../atoms/ArticleImage/ArticleImage';
import { operationalScrollbars } from '../../../design-system/tokens';
import {
  isQualitativeAnalysisAiApplied
} from '../../../utils/aiAnalysisStatus';
import { getEffectiveTier } from '../../../utils/effectiveTier';
import { formatDuration, getBrandDurationSeconds, getTotalDurationSeconds, isTimeBasedMedia, parseDuration } from '../../../utils/editorialOccupancy';

const compactFormatter = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  maximumFractionDigits: 1
});

const integerFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0
});

const TABLE_LAYOUT_STORAGE_KEY = 'mensure.measurementTable.layout.v1';

const DEFAULT_COLUMN_KEYS = [
  'article',
  'originalLink',
  'date',
  'source',
  'author',
  'media',
  'tier',
  'sentiment',
  'exposure',
  'editorialOrigin',
  'spokesperson',
  'keyMessage',
  'editorial',
  'valuation',
  'audienceDaily',
  'audienceMonthly',
  'centimetragem',
  'views',
  'likes',
  'comments',
  'shares',
  'tags',
  'smartScore',
  'kpia'
];

const formatCompact = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '-';
  return compactFormatter.format(numericValue);
};

const formatCurrency = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '-';
  return `R$ ${compactFormatter.format(numericValue)}`;
};

const formatDate = (value) => {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC'
    });
  } catch {
    return value;
  }
};

const getSiteName = (article) => {
  const articleUrl = article.siteUrl || article.url;
  return article.siteName || article['site-name'] || article.site_name || article.source ||
    (articleUrl ? (() => {
      try {
        return new URL(articleUrl).hostname.replace('www.', '');
      } catch {
        return '';
      }
    })() : '');
};

const getOriginalUrl = (article) => (
  article?.siteUrl ||
  article?.url ||
  article?.videoUrl ||
  article?.originalUrl ||
  article?.sourceUrl ||
  article?.pdfUrl ||
  article?.image ||
  ''
);

const getOriginalLinkLabel = (url) => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    const readableUrl = `${parsedUrl.hostname.replace(/^www\./, '')}${parsedUrl.pathname}`;
    return readableUrl.length > 58 ? `${readableUrl.slice(0, 55)}...` : readableUrl;
  } catch {
    return String(url).length > 58 ? `${String(url).slice(0, 55)}...` : String(url);
  }
};

const getTierValue = (article) => {
  const tier = getEffectiveTier(article);
  if (!tier) return '';
  const match = String(tier).match(/[123]/);
  return match ? match[0] : '';
};

const getEditorialOrigin = (article) => (
  article.section ||
  article.editoria ||
  article.editorialSection ||
  article.category ||
  article.domainInfo?.category ||
  article.domainInfo?.tipo ||
  ''
);

const getAuthorValue = (article) => (
  article.author ||
  article.creator ||
  article.channelTitle ||
  article.domainInfo?.author ||
  article.domainInfo?.cidade ||
  ''
);

const getBrandEntity = (occupancy = {}) => (
  Array.isArray(occupancy.entityBreakdown)
    ? occupancy.entityBreakdown.find(entity => entity?.type === 'brand') || null
    : null
);

const MANUAL_SPOKESPERSON_PLACEHOLDER = 'Identificado manualmente';

const getSpokespersonName = (article = {}) => {
  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};
  const brandEntity = getBrandEntity(occupancy);

  return [
    occupancy.spokespersonFound,
    brandEntity?.spokespersonFound,
    article.spokespersonFound
  ].find(value => value && value !== MANUAL_SPOKESPERSON_PLACEHOLDER) || null;
};

const getSpokespersonValue = (article) => {
  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};
  const brandEntity = getBrandEntity(occupancy);

  if (occupancy.spokespersonManualOverride === true) {
    return occupancy.spokespersonFound ? 'yes' : 'no';
  }

  return occupancy.spokespersonFound || brandEntity?.spokespersonFound || article.spokespersonFound ? 'yes' : 'no';
};

const getKeyMessageValue = (article) => {
  const occupancy = article.editorialOccupancy || article.onlineCentimetragem || {};
  const brandEntity = getBrandEntity(occupancy);
  const occupancyMessages = Array.isArray(occupancy.keyMessagesFound) ? occupancy.keyMessagesFound : [];
  const brandMessages = Array.isArray(brandEntity?.keyMessagesFound) ? brandEntity.keyMessagesFound : [];
  const articleMessages = Array.isArray(article.keyMessagesFound) ? article.keyMessagesFound : [];

  if (occupancy.keyMessagesManualOverride === true) {
    return occupancyMessages.length > 0 ? 'yes' : 'no';
  }

  return occupancyMessages.length > 0 || brandMessages.length > 0 || articleMessages.length > 0 ? 'yes' : 'no';
};

const getEditorialOriginMeasurementValue = (article) => (
  article.editorialOrigin?.origin ||
  article.smartScore?.editorialOrigin?.origin ||
  'unknown'
);

const qualitativeMeasurementFields = [
  {
    key: 'sentiment',
    label: 'Sentimento',
    defaultValue: 'positive',
    getValue: article => article.sentiment || 'neutral',
    toPatch: value => ({ sentiment: value }),
    options: [
      { value: 'positive', label: 'Positivo' },
      { value: 'neutral', label: 'Neutro' },
      { value: 'negative', label: 'Negativo' }
    ]
  },
  {
    key: 'exposure',
    label: 'Exposicao',
    defaultValue: 'dedicated',
    getValue: article => article.exposure || 'none',
    toPatch: value => ({ exposure: value }),
    options: [
      { value: 'dedicated', label: 'Dedicada' },
      { value: 'shared', label: 'Compartilhada' },
      { value: 'mention', label: 'Citacao' },
      { value: 'none', label: 'Nenhuma' }
    ]
  },
  {
    key: 'tier',
    label: 'Tier',
    defaultValue: '1',
    getValue: article => getTierValue(article) || '3',
    toPatch: value => ({ tier: Number(value || 3) }),
    options: [
      { value: '1', label: 'Tier 1' },
      { value: '2', label: 'Tier 2' },
      { value: '3', label: 'Tier 3' }
    ]
  },
  {
    key: 'editorialOrigin',
    label: 'Origem editorial',
    defaultValue: 'unknown',
    getValue: getEditorialOriginMeasurementValue,
    toPatch: value => ({
      'editorialOrigin.origin': value === 'unknown' ? null : value,
      'editorialOrigin.confidence': value === 'unknown' ? null : 'high',
      'editorialOrigin.manualOverride': value !== 'unknown',
      'smartScore.editorialOrigin.origin': value === 'unknown' ? null : value,
      'smartScore.editorialOrigin.confidence': value === 'unknown' ? null : 'high',
      'smartScore.editorialOrigin.manualOverride': value !== 'unknown'
    }),
    options: [
      { value: 'unknown', label: 'Nao definida' },
      { value: 'spontaneous', label: 'Espontanea' },
      { value: 'provoked', label: 'Provocada' },
      { value: 'probably_provoked', label: 'Prov. provocada' }
    ]
  },
  {
    key: 'spokesperson',
    label: 'Porta-voz',
    defaultValue: 'no',
    getValue: getSpokespersonValue,
    toPatch: (value, article) => ({
      'onlineCentimetragem.spokespersonFound': value === 'yes' ? getSpokespersonName(article) : null,
      'onlineCentimetragem.spokespersonManualOverride': true
    }),
    options: [
      { value: 'yes', label: 'Sim' },
      { value: 'no', label: 'Nao' }
    ]
  },
  {
    key: 'keyMessage',
    label: 'Mensagem-chave',
    defaultValue: 'no',
    getValue: getKeyMessageValue,
    toPatch: value => ({
      'onlineCentimetragem.keyMessagesFound': value === 'yes' ? ['Identificada manualmente'] : [],
      'onlineCentimetragem.keyMessagesManualOverride': true
    }),
    options: [
      { value: 'yes', label: 'Sim' },
      { value: 'no', label: 'Nao' }
    ]
  }
];

const getAudienceValue = (article) => (
  article.audienceMonthly ||
  article.monthlyAudience ||
  article.audience ||
  article.subscribersNumber ||
  article.followersNumber ||
  0
);

const getAudienceDailyValue = (article) => (
  article.audience ||
  article.subscribersNumber ||
  article.followersNumber ||
  0
);

const getAudienceMonthlyValue = (article) => (
  article.audienceMonthly ||
  article.monthlyAudience ||
  0
);

const isPrintMediaArticle = (article) => ['Jornal', 'Revista'].includes(article?.mediaType);

const parseMeasurementNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatMeasurementInputValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? String(numericValue).replace('.', ',') : '';
};

const getPrintCentimetragemValue = (article) => {
  const occupancy = article?.editorialOccupancy || article?.onlineCentimetragem || {};
  return (
    occupancy.valueCm2 ||
    occupancy.editorialAreaBrandRelevantCm2 ||
    occupancy.editorialAreaTotalCm2 ||
    0
  );
};

const getSmartScoreValue = (article) => article.smartScore?.score ?? article.smartScoreValue ?? null;

const getTagsText = (article) => (article.tags || [])
  .map(tag => tag.name || tag.label || tag._id || tag)
  .filter(Boolean)
  .join(', ');

const getKpiaStatus = (article) => (isQualitativeAnalysisAiApplied(article) ? 'Aplicada' : 'Pendente');

const normalizeText = (value) => String(value || '').toLowerCase();

const loadSavedColumnLayout = () => {
  if (typeof window === 'undefined') return null;

  try {
    const saved = window.localStorage.getItem(TABLE_LAYOUT_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeColumnLayout = (columns, layout) => {
  const availableKeys = new Set(columns.map(column => column.key));
  const layoutItems = Array.isArray(layout) ? layout : [];
  const normalized = [];
  const usedKeys = new Set();

  layoutItems.forEach((item) => {
    if (!item?.key || !availableKeys.has(item.key) || usedKeys.has(item.key)) return;
    normalized.push({
      key: item.key,
      visible: item.visible !== false
    });
    usedKeys.add(item.key);
  });

  if (layoutItems.length > 0 && availableKeys.has('originalLink') && !usedKeys.has('originalLink')) {
    const articleIndex = normalized.findIndex(item => item.key === 'article');
    const originalLinkItem = { key: 'originalLink', visible: true };
    if (articleIndex >= 0) {
      normalized.splice(articleIndex + 1, 0, originalLinkItem);
    } else {
      normalized.push(originalLinkItem);
    }
    usedKeys.add('originalLink');
  }

  DEFAULT_COLUMN_KEYS.forEach((key) => {
    if (!availableKeys.has(key) || usedKeys.has(key)) return;
    normalized.push({ key, visible: true });
    usedKeys.add(key);
  });

  columns.forEach((column) => {
    if (usedKeys.has(column.key)) return;
    normalized.push({ key: column.key, visible: true });
  });

  if (normalized.length > 0 && !normalized.some(item => item.visible)) {
    normalized[0] = { ...normalized[0], visible: true };
  }

  return normalized;
};

const persistColumnLayout = (layout) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(TABLE_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {}
};

const getQualitativeFieldLabel = (fieldKey, value) => {
  const field = qualitativeMeasurementFields.find(item => item.key === fieldKey);
  const option = field?.options.find(item => String(item.value) === String(value));
  return option?.label || value || '-';
};

const getColumnExportValue = (article, key) => {
  switch (key) {
    case 'article':
      return article.title || '';
    case 'originalLink':
      return getOriginalUrl(article);
    case 'date':
      return formatDate(article.date || article.publishedAt || article.createdAt);
    case 'source':
      return getSiteName(article) || 'Fonte desconhecida';
    case 'author':
      return getAuthorValue(article);
    case 'media':
      return article.mediaType || '-';
    case 'tier':
      return getQualitativeFieldLabel('tier', getTierValue(article) || '3');
    case 'sentiment':
      return getQualitativeFieldLabel('sentiment', article.sentiment || 'neutral');
    case 'exposure':
      return getQualitativeFieldLabel('exposure', article.exposure || 'none');
    case 'editorialOrigin':
      return getQualitativeFieldLabel('editorialOrigin', getEditorialOriginMeasurementValue(article));
    case 'spokesperson':
      return getQualitativeFieldLabel('spokesperson', getSpokespersonValue(article));
    case 'keyMessage':
      return getQualitativeFieldLabel('keyMessage', getKeyMessageValue(article));
    case 'editorial':
      return getEditorialOrigin(article);
    case 'valuation':
      return formatCurrency(article.valuation);
    case 'audience':
      return formatCompact(getAudienceValue(article));
    case 'audienceDaily':
      return formatCompact(getAudienceDailyValue(article));
    case 'audienceMonthly':
      return formatCompact(getAudienceMonthlyValue(article));
    case 'centimetragem':
      if (isTimeBasedMedia(article)) return getBrandDurationSeconds(article) > 0 ? formatDuration(getBrandDurationSeconds(article)) : '-';
      return getPrintCentimetragemValue(article) > 0
        ? `${integerFormatter.format(getPrintCentimetragemValue(article))} cm²`
        : '-';
    case 'views':
      return formatCompact(article.viewsNumber);
    case 'likes':
      return formatCompact(article.likesNumber);
    case 'comments':
      return formatCompact(article.commentsNumber);
    case 'shares':
      return formatCompact(article.sharesNumber);
    case 'tags':
      return getTagsText(article);
    case 'smartScore': {
      const smartScore = getSmartScoreValue(article);
      return smartScore !== null && smartScore !== undefined && Number.isFinite(Number(smartScore))
        ? integerFormatter.format(Number(smartScore))
        : smartScore ?? '-';
    }
    case 'kpia':
      return getKpiaStatus(article);
    default:
      return '';
  }
};

const exportRowsToExcel = (rows, columns) => {
  const header = columns.map(column => column.label);
  const data = rows.map(article => columns.reduce((acc, column) => ({
    ...acc,
    [column.label]: getColumnExportValue(article, column.key)
  }), {}));
  const worksheet = XLSX.utils.json_to_sheet(data, { header });
  worksheet['!cols'] = columns.map(column => ({
    wch: Math.max(10, Math.min(48, Math.round(Number(column.width || 120) / 8)))
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Mensuracao');

  const fileName = `mensuracao_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(workbook, fileName);
  return fileName;
};

const getSortValue = (article, key) => {
  switch (key) {
    case 'article':
      return normalizeText(article.title);
    case 'originalLink':
      return normalizeText(getOriginalUrl(article));
    case 'date':
      return article.date || article.publishedAt || article.createdAt || '';
    case 'source':
      return normalizeText(getSiteName(article));
    case 'author':
      return normalizeText(getAuthorValue(article));
    case 'media':
      return normalizeText(article.mediaType);
    case 'tier':
      return Number(getTierValue(article) || 0);
    case 'sentiment':
      return normalizeText(article.sentiment);
    case 'exposure':
      return normalizeText(article.exposure);
    case 'editorialOrigin':
      return normalizeText(getEditorialOriginMeasurementValue(article));
    case 'spokesperson':
      return normalizeText(getSpokespersonValue(article));
    case 'keyMessage':
      return normalizeText(getKeyMessageValue(article));
    case 'editorial':
      return normalizeText(getEditorialOrigin(article));
    case 'valuation':
      return Number(article.valuation || 0);
    case 'audience':
      return Number(getAudienceValue(article) || 0);
    case 'audienceDaily':
      return Number(getAudienceDailyValue(article) || 0);
    case 'audienceMonthly':
      return Number(getAudienceMonthlyValue(article) || 0);
    case 'centimetragem':
      return isTimeBasedMedia(article) ? getBrandDurationSeconds(article) : Number(getPrintCentimetragemValue(article) || 0);
    case 'views':
      return Number(article.viewsNumber || 0);
    case 'likes':
      return Number(article.likesNumber || 0);
    case 'comments':
      return Number(article.commentsNumber || 0);
    case 'shares':
      return Number(article.sharesNumber || 0);
    case 'tags':
      return normalizeText(getTagsText(article));
    case 'smartScore':
      return Number(getSmartScoreValue(article) ?? -999);
    case 'kpia':
      return normalizeText(getKpiaStatus(article));
    default:
      return '';
  }
};

const getFilterValue = (article, key) => {
  switch (key) {
    case 'article':
      return article.title || '';
    case 'originalLink':
      return getOriginalUrl(article);
    case 'date':
      return formatDate(article.date || article.publishedAt || article.createdAt);
    case 'source':
      return getSiteName(article);
    case 'author':
      return getAuthorValue(article);
    case 'media':
      return article.mediaType || '';
    case 'tier':
      return getTierValue(article);
    case 'sentiment':
      return article.sentiment || 'neutral';
    case 'exposure':
      return article.exposure || 'none';
    case 'editorialOrigin':
      return getEditorialOriginMeasurementValue(article);
    case 'spokesperson':
      return getSpokespersonValue(article);
    case 'keyMessage':
      return getKeyMessageValue(article);
    case 'editorial':
      return getEditorialOrigin(article) || '';
    case 'tags':
      return getTagsText(article);
    case 'kpia':
      return getKpiaStatus(article);
    default:
      return '';
  }
};

const nativeSelectMenuSx = {
  colorScheme: 'dark',
  '& option': {
    backgroundColor: '#202124',
    color: '#f8fafc'
  },
  '& option:checked': {
    backgroundColor: '#334155',
    color: '#ffffff'
  }
};

const ColumnFilter = ({ type, value, options = [], placeholder, onChange }) => {
  if (!type) return <Box sx={{ height: 30 }} />;

  if (type === 'select') {
    return (
      <Box
        component="select"
        value={value || ''}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          width: '100%',
          height: 24,
          border: 0,
          borderBottom: '1px solid rgba(148,163,184,0.5)',
          bgcolor: 'transparent',
          color: 'text.secondary',
          font: 'inherit',
          fontSize: '0.72rem',
          outline: 'none',
          cursor: 'pointer',
          ...nativeSelectMenuSx
        }}
      >
        <option value="">Todos</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Box>
    );
  }

  return (
    <Box
      component="input"
      value={value || ''}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder || 'Filtrar'}
      sx={{
        width: '100%',
        height: 24,
        border: 0,
        borderBottom: '1px solid rgba(148,163,184,0.5)',
        bgcolor: 'transparent',
        color: 'text.primary',
        font: 'inherit',
        fontSize: '0.72rem',
        outline: 'none',
        '&::placeholder': {
          color: 'text.secondary',
          opacity: 0.8
        }
      }}
    />
  );
};

ColumnFilter.propTypes = {
  type: PropTypes.oneOf(['text', 'select']),
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

const ColumnSortButton = ({ label, active, direction, onClick }) => {
  let Icon = SortNeutralIcon;
  if (active) {
    Icon = direction === 'asc' ? SortAscIcon : SortDescIcon;
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.45,
        width: '100%',
        minWidth: 0,
        color: active ? 'text.primary' : 'text.secondary',
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: active ? 720 : 620,
        lineHeight: 1.2
      }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      <Icon
        fontSize="inherit"
        sx={{
          color: active ? 'text.primary' : 'text.disabled',
          fontSize: 14,
          flexShrink: 0
        }}
      />
    </Box>
  );
};

ColumnSortButton.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onClick: PropTypes.func.isRequired
};

const SheetCellText = ({ children, title, muted = false, strong = false }) => {
  const value = children === null || children === undefined || children === '' ? '-' : children;

  return (
    <Box
      component="span"
      translate="no"
      title={title || (typeof value === 'string' ? value : undefined)}
      sx={{
        display: 'block',
        color: muted || value === '-' ? 'text.secondary' : 'text.primary',
        fontSize: '0.745rem',
        fontWeight: strong ? 560 : 450,
        lineHeight: 1.35,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {value}
    </Box>
  );
};

SheetCellText.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  muted: PropTypes.bool,
  strong: PropTypes.bool
};

const OriginalLinkCell = ({ url }) => {
  if (!url) {
    return <SheetCellText muted>-</SheetCellText>;
  }

  const label = getOriginalLinkLabel(url) || 'Abrir original';

  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      translate="no"
      title={url}
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.45,
        maxWidth: '100%',
        minWidth: 0,
        color: 'primary.light',
        fontSize: '0.745rem',
        fontWeight: 560,
        lineHeight: 1.35,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline'
        }
      }}
    >
      <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
      <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </Box>
  );
};

OriginalLinkCell.propTypes = {
  url: PropTypes.string
};

const NativeCheckbox = ({ checked, indeterminate = false, onChange, onClick, ariaLabel }) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <Box
      component="input"
      ref={inputRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        width: 14,
        height: 14,
        m: 0,
        accentColor: '#9ca3af',
        cursor: 'pointer'
      }}
    />
  );
};

NativeCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string
};

const QualitativeSelectCell = ({ article, fieldKey, disabled = false, onChange }) => {
  const field = qualitativeMeasurementFields.find(item => item.key === fieldKey);
  if (!field) return <SheetCellText>-</SheetCellText>;

  return (
    <Box
      component="select"
      value={field.getValue(article) || field.defaultValue}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange?.(article, field, event.target.value)}
      sx={{
        width: '100%',
        height: 24,
        border: 0,
        borderBottom: '1px solid rgba(148,163,184,0.26)',
        bgcolor: 'transparent',
        color: disabled ? 'text.disabled' : 'text.primary',
        font: 'inherit',
        fontSize: '0.745rem',
        fontWeight: 520,
        outline: 'none',
        cursor: disabled ? 'wait' : 'pointer',
        ...nativeSelectMenuSx
      }}
    >
      {field.options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </Box>
  );
};

QualitativeSelectCell.propTypes = {
  article: PropTypes.object.isRequired,
  fieldKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func
};

const bulkControlSx = {
  height: 28,
  minWidth: 0,
  border: '1px solid rgba(148,163,184,0.28)',
  borderRadius: 1,
  bgcolor: 'transparent',
  color: 'text.primary',
  font: 'inherit',
  fontSize: '0.74rem',
  outline: 'none',
  px: 0.8,
  ...nativeSelectMenuSx
};

const MeasurementBulkValueControl = ({ field, value, onChange }) => {
  if (!field) return null;

  return (
    <Box
      component="select"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ ...bulkControlSx, width: 160, cursor: 'pointer' }}
    >
      {field.options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </Box>
  );
};

MeasurementBulkValueControl.propTypes = {
  field: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired
};

const MeasurementBulkBar = ({
  selectedCount,
  edits,
  saving,
  error,
  onAddEdit,
  onRemoveEdit,
  onFieldChange,
  onValueChange,
  onApply
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (selectedCount <= 0) {
      setDialogOpen(false);
    }
  }, [selectedCount]);

  if (selectedCount <= 0) return null;

  const handleApply = async () => {
    const success = await onApply();
    if (success) {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          px: 0.8,
          py: 0.65,
          borderBottom: '1px solid rgba(148,163,184,0.16)',
          bgcolor: 'rgba(148,163,184,0.045)'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.1} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 620 }}>
            {selectedCount} selecionado{selectedCount === 1 ? '' : 's'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setDialogOpen(true)}
            sx={{
              height: 28,
              textTransform: 'none',
              borderRadius: 1,
              boxShadow: 'none',
              fontSize: '0.74rem',
              fontWeight: 620
            }}
          >
            Mensurar selecionados
          </Button>
          {error && (
            <Typography variant="caption" color="error.main">
              {error}
            </Typography>
          )}
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 720 }}>
          Mensuracao qualitativa em lote
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aplique KPIs qualitativos aos {selectedCount} artigo{selectedCount === 1 ? '' : 's'} selecionado{selectedCount === 1 ? '' : 's'}.
          </Typography>

          <Stack spacing={1.2}>
            {edits.map((edit, index) => {
              const selectedField = qualitativeMeasurementFields.find(field => field.key === edit.field) || qualitativeMeasurementFields[0];

              return (
                <Stack key={edit.id} direction="row" alignItems="center" spacing={0.8}>
                  <Box
                    component="select"
                    value={edit.field}
                    onChange={(event) => onFieldChange(edit.id, event.target.value)}
                    sx={{ ...bulkControlSx, width: 180, cursor: 'pointer' }}
                  >
                    {qualitativeMeasurementFields.map(field => (
                      <option key={field.key} value={field.key}>{field.label}</option>
                    ))}
                  </Box>

                  <MeasurementBulkValueControl
                    field={selectedField}
                    value={edit.value}
                    onChange={(value) => onValueChange(edit.id, value)}
                  />

                  {edits.length > 1 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onRemoveEdit(edit.id)}
                      sx={{ minWidth: 26, height: 28, px: 0.6, textTransform: 'none', color: 'text.secondary' }}
                    >
                      x
                    </Button>
                  )}

                  {index < edits.length - 1 && (
                    <Typography variant="caption" color="text.disabled">+</Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>

          <Button
            size="small"
            variant="text"
            onClick={onAddEdit}
            disabled={saving}
            sx={{ mt: 1.4, height: 28, textTransform: 'none', color: 'text.secondary', borderRadius: 1 }}
          >
            + KPI qualitativo
          </Button>

          {error && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 1, boxShadow: 'none', fontWeight: 620 }}
          >
            {saving ? 'Aplicando...' : 'Aplicar KPIs'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

MeasurementBulkBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  edits: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })).isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onAddEdit: PropTypes.func.isRequired,
  onRemoveEdit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
};

const ColumnLayoutDialog = ({
  open,
  columns,
  layout,
  onClose,
  onToggle,
  onMove,
  onReset
}) => {
  const columnByKey = React.useMemo(() => new Map(columns.map(column => [column.key, column])), [columns]);
  const layoutRows = layout
    .map(item => ({ ...item, column: columnByKey.get(item.key) }))
    .filter(item => item.column);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 760 }}>
        Configurar planilha
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Escolha as colunas visiveis e ajuste a ordem. A visualizacao fica salva neste navegador.
        </Typography>

        <Stack spacing={0.55}>
          {layoutRows.map((item, index) => (
            <Box
              key={item.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr auto auto',
                alignItems: 'center',
                gap: 0.8,
                minHeight: 34,
                px: 1,
                py: 0.45,
                border: '1px solid rgba(148,163,184,0.14)',
                borderRadius: 1,
                bgcolor: item.visible ? 'rgba(148,163,184,0.055)' : 'transparent'
              }}
            >
              <NativeCheckbox
                checked={item.visible}
                onChange={() => onToggle(item.key)}
                ariaLabel={`Mostrar ${item.column.label}`}
              />
              <Typography
                variant="body2"
                title={item.column.label}
                sx={{
                  minWidth: 0,
                  fontWeight: item.visible ? 650 : 500,
                  color: item.visible ? 'text.primary' : 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.column.label}
              </Typography>
              <Button
                size="small"
                variant="text"
                disabled={index === 0}
                onClick={() => onMove(item.key, -1)}
                sx={{ minWidth: 28, width: 28, height: 26, p: 0, color: 'text.secondary' }}
              >
                <SortAscIcon sx={{ fontSize: 16 }} />
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={index === layoutRows.length - 1}
                onClick={() => onMove(item.key, 1)}
                sx={{ minWidth: 28, width: 28, height: 26, p: 0, color: 'text.secondary' }}
              >
                <SortDescIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.2 }}>
        <Button onClick={onReset} sx={{ textTransform: 'none', mr: 'auto' }}>
          Restaurar padrao
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', borderRadius: 1, boxShadow: 'none' }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ColumnLayoutDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  layout: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    visible: PropTypes.bool.isRequired
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
};

const MeasurementInspectorDrawer = ({
  open,
  article,
  onClose,
  onSave,
  onArticleClick,
  onEdit
}) => {
  const theme = useTheme();
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!article) return;
    setForm({
      tier: getTierValue(article) || '3',
      sentiment: ['positive', 'neutral', 'negative'].includes(article.sentiment) ? article.sentiment : 'neutral',
      exposure: article.exposure || 'none',
      editorialOrigin: getEditorialOriginMeasurementValue(article),
      spokesperson: getSpokespersonValue(article),
      keyMessage: getKeyMessageValue(article),
      valuation: formatMeasurementInputValue(article.valuation),
      audienceDaily: formatMeasurementInputValue(getAudienceDailyValue(article)),
      audienceMonthly: formatMeasurementInputValue(getAudienceMonthlyValue(article)),
      centimetragem: formatMeasurementInputValue(getPrintCentimetragemValue(article)),
      totalDuration: getTotalDurationSeconds(article) > 0 ? formatDuration(getTotalDurationSeconds(article)) : '',
      brandDuration: getBrandDurationSeconds(article) > 0 ? formatDuration(getBrandDurationSeconds(article)) : ''
    });
    setError('');
  }, [article]);

  const updateField = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const handleSave = async () => {
    if (!article || !onSave) return;
    setSaving(true);
    setError('');

    const patch = qualitativeMeasurementFields.reduce((acc, field) => ({
      ...acc,
      ...field.toPatch(form[field.key] ?? field.defaultValue, article)
    }), {});

    const valuation = parseMeasurementNumber(form.valuation);
    const audienceDaily = parseMeasurementNumber(form.audienceDaily);
    const audienceMonthly = parseMeasurementNumber(form.audienceMonthly);

    Object.assign(patch, {
      valuation,
      audience: audienceDaily,
      audienceMonthly
    });

    if (isTimeBasedMedia(article)) {
      const totalDurationSeconds = parseDuration(form.totalDuration);
      const brandDurationSeconds = Math.min(parseDuration(form.brandDuration), totalDurationSeconds || Infinity);
      Object.assign(patch, {
        durationSeconds: totalDurationSeconds,
        'onlineCentimetragem.enabled': totalDurationSeconds > 0 && brandDurationSeconds > 0,
        'onlineCentimetragem.status': totalDurationSeconds > 0 && brandDurationSeconds > 0 ? 'manual' : null,
        'onlineCentimetragem.measurementUnit': 'time',
        'onlineCentimetragem.totalDurationSeconds': totalDurationSeconds,
        'onlineCentimetragem.brandDurationSeconds': brandDurationSeconds,
        'onlineCentimetragem.brandParticipationPercent': totalDurationSeconds > 0 ? brandDurationSeconds / totalDurationSeconds : 0,
        'onlineCentimetragem.method': 'manual-time',
        'onlineCentimetragem.aiUsed': false
      });
    } else if (isPrintMediaArticle(article)) {
      const centimetragem = parseMeasurementNumber(form.centimetragem);

      Object.assign(patch, {
        'onlineCentimetragem.enabled': centimetragem > 0,
        'onlineCentimetragem.status': centimetragem > 0 ? 'manual' : null,
        'onlineCentimetragem.valueCm2': centimetragem,
        'onlineCentimetragem.editorialAreaTotalCm2': centimetragem,
        'onlineCentimetragem.editorialAreaBrandRelevantCm2': centimetragem,
        'onlineCentimetragem.brandParticipationPercent': centimetragem > 0 ? 1 : 0,
        'onlineCentimetragem.method': centimetragem > 0 ? 'manual' : null,
        'onlineCentimetragem.aiUsed': false
      });
    }

    try {
      await onSave(article._id, patch);
      onClose();
    } catch (saveError) {
      console.error('Erro ao salvar mensuracao:', saveError);
      setError(saveError.response?.data?.message || 'Erro ao salvar campos de mensuracao.');
    } finally {
      setSaving(false);
    }
  };

  const siteName = article ? getSiteName(article) : '';
  const smartScore = article ? getSmartScoreValue(article) : null;
  const isPrintMedia = isPrintMediaArticle(article);
  const isTimeMedia = isTimeBasedMedia(article);
  const renderQualitativeControl = (fieldKey) => {
    const field = qualitativeMeasurementFields.find(item => item.key === fieldKey);
    if (!field) return null;

    return (
      <FormControl fullWidth size="small">
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={form[field.key] ?? field.defaultValue}
          onChange={(event) => updateField(field.key, event.target.value)}
        >
          {field.options.map(option => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderPrintMetricField = (fieldKey, label, helperText) => {
    if (fieldKey === 'centimetragem' && !isPrintMedia) return null;

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={form[fieldKey] ?? ''}
        onChange={(event) => updateField(fieldKey, event.target.value)}
        inputProps={{ inputMode: 'decimal' }}
        helperText={helperText}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420, md: 460 },
          bgcolor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.divider}`
        }
      }}
    >
      {!article ? null : (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2.5, pb: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
              Mensuracao
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.2, minWidth: 0 }}>
              <Box sx={{ width: 72, height: 54, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0 }}>
                <ArticleImage src={article} alt={article.title} variant="thumbnail" sx={{ width: 72, height: 54 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" translate="no" sx={{ fontWeight: 820, lineHeight: 1.25 }}>
                  {article.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" translate="no">
                  {siteName || 'Fonte desconhecida'} - {formatDate(article.date || article.publishedAt || article.createdAt)}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => onArticleClick?.(article)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Ver artigo
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => onEdit?.(article)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Editar completo
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, ...operationalScrollbars.thin }}>
            <Stack spacing={2.2}>
              {(
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                    Campos operacionais
                  </Typography>

                  <Stack direction="row" spacing={1.5}>
                    {renderPrintMetricField('valuation', 'Valoração', 'R$')}
                    {renderPrintMetricField('audienceDaily', 'Audiência dia', 'Dia')}
                  </Stack>

                  {renderPrintMetricField('audienceMonthly', 'Audiência mês', 'Mês')}
                  {renderPrintMetricField('centimetragem', 'Área Editorial', 'cm² da área editorial')}
                  {isTimeMedia && (
                    <Stack direction="row" spacing={1.5}>
                      {renderPrintMetricField('totalDuration', 'Duração total', 'mm:ss ou hh:mm:ss')}
                      {renderPrintMetricField('brandDuration', 'Minutagem da marca', 'mm:ss ou hh:mm:ss')}
                    </Stack>
                  )}

                  <Divider />
                </>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
                KPIs qualitativos
              </Typography>

              <Stack direction="row" spacing={1.5}>
                {renderQualitativeControl('sentiment')}
                {renderQualitativeControl('exposure')}
              </Stack>

              <Stack direction="row" spacing={1.5}>
                {renderQualitativeControl('tier')}
                {renderQualitativeControl('editorialOrigin')}
              </Stack>

              <Stack direction="row" spacing={1.5}>
                {renderQualitativeControl('spokesperson')}
                {renderQualitativeControl('keyMessage')}
              </Stack>

              <Divider />

              <Stack direction="row" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">SmartScore</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 840 }}>
                    {smartScore !== null && smartScore !== undefined ? integerFormatter.format(Number(smartScore)) : '-'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">KPIA</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 620, color: 'text.secondary' }}>
                    {isQualitativeAnalysisAiApplied(article) ? 'Aplicada' : 'Pendente'}
                  </Typography>
                </Box>
              </Stack>

              {error && (
                <Typography variant="body2" color="error.main">
                  {error}
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 2, display: 'flex', gap: 1.2, justifyContent: 'flex-end' }}>
            <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 760 }}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

MeasurementInspectorDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  article: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onArticleClick: PropTypes.func,
  onEdit: PropTypes.func
};

const ArticleMeasurementTable = ({
  articles,
  loading = false,
  selectedArticles = [],
  selectionMode = false,
  onSelectArticle,
  onArticleClick,
  onEdit,
  onMeasurementSave,
  onMeasurementBulkSave
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [sortConfig, setSortConfig] = React.useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = React.useState({});
  const [inspectorArticleId, setInspectorArticleId] = React.useState(null);
  const [columnDialogOpen, setColumnDialogOpen] = React.useState(false);
  const [columnLayout, setColumnLayout] = React.useState(() => loadSavedColumnLayout());
  const bulkEditIdRef = React.useRef(1);
  const createBulkEdit = React.useCallback((fieldKey = 'sentiment') => {
    const field = qualitativeMeasurementFields.find(item => item.key === fieldKey) || qualitativeMeasurementFields[0];
    const id = `bulk-field-${bulkEditIdRef.current}`;
    bulkEditIdRef.current += 1;
    return { id, field: field.key, value: field.defaultValue };
  }, []);
  const [bulkEdits, setBulkEdits] = React.useState(() => [{ id: 'bulk-field-0', field: 'sentiment', value: 'positive' }]);
  const [bulkSaving, setBulkSaving] = React.useState(false);
  const [bulkError, setBulkError] = React.useState('');
  const [inlineSaving, setInlineSaving] = React.useState({});
  const selectedSet = React.useMemo(() => new Set(selectedArticles || []), [selectedArticles]);

  const filterOptions = React.useMemo(() => {
    const unique = (key, mapper = value => value) => Array.from(new Set(
      articles
        .map(article => mapper(getFilterValue(article, key)))
        .filter(Boolean)
    )).sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }));

    return {
      media: unique('media').map(value => ({ value, label: value })),
      tier: qualitativeMeasurementFields.find(field => field.key === 'tier')?.options || [],
      sentiment: qualitativeMeasurementFields.find(field => field.key === 'sentiment')?.options || [],
      exposure: qualitativeMeasurementFields.find(field => field.key === 'exposure')?.options || [],
      editorialOrigin: qualitativeMeasurementFields.find(field => field.key === 'editorialOrigin')?.options || [],
      spokesperson: qualitativeMeasurementFields.find(field => field.key === 'spokesperson')?.options || [],
      keyMessage: qualitativeMeasurementFields.find(field => field.key === 'keyMessage')?.options || [],
      kpia: [
        { value: 'Aplicada', label: 'Aplicada' },
        { value: 'Pendente', label: 'Pendente' }
      ]
    };
  }, [articles]);

  const columns = React.useMemo(() => ([
    { key: 'article', label: 'Titulo', width: 330, filter: 'text', placeholder: 'Titulo' },
    { key: 'originalLink', label: 'Links Originais', width: 230, filter: 'text', placeholder: 'Link' },
    { key: 'date', label: 'Data', width: 112, filter: 'text', placeholder: 'Data' },
    { key: 'source', label: 'Fonte', width: 170, filter: 'text', placeholder: 'Fonte' },
    { key: 'author', label: 'Autor', width: 165, filter: 'text', placeholder: 'Autor' },
    { key: 'media', label: 'Midia', width: 120, filter: 'select', options: filterOptions.media },
    { key: 'tier', label: 'Tier', width: 96, filter: 'select', options: filterOptions.tier },
    { key: 'sentiment', label: 'Sentimento', width: 128, filter: 'select', options: filterOptions.sentiment },
    { key: 'exposure', label: 'Exposicao', width: 140, filter: 'select', options: filterOptions.exposure },
    { key: 'editorialOrigin', label: 'Origem editorial', width: 150, filter: 'select', options: filterOptions.editorialOrigin },
    { key: 'spokesperson', label: 'Porta-voz', width: 115, filter: 'select', options: filterOptions.spokesperson },
    { key: 'keyMessage', label: 'Mensagem-chave', width: 145, filter: 'select', options: filterOptions.keyMessage },
    { key: 'editorial', label: 'Editoria', width: 140, filter: 'text', placeholder: 'Origem' },
    { key: 'valuation', label: 'Valor', width: 120 },
    { key: 'audienceDaily', label: 'Audiência dia', width: 126 },
    { key: 'audienceMonthly', label: 'Audiência mês', width: 132 },
    { key: 'centimetragem', label: 'Ocupação Editorial', width: 150 },
    { key: 'views', label: 'Views', width: 108 },
    { key: 'likes', label: 'Likes', width: 102 },
    { key: 'comments', label: 'Comentarios', width: 124 },
    { key: 'shares', label: 'Compart.', width: 112 },
    { key: 'tags', label: 'Tags', width: 230, filter: 'text', placeholder: 'Tags' },
    { key: 'smartScore', label: 'Score', width: 92 },
    { key: 'kpia', label: 'KPIA', width: 104, filter: 'select', options: filterOptions.kpia }
  ]), [filterOptions]);

  const resolvedColumnLayout = React.useMemo(() => (
    normalizeColumnLayout(columns, columnLayout)
  ), [columns, columnLayout]);

  const orderedColumns = React.useMemo(() => {
    const columnByKey = new Map(columns.map(column => [column.key, column]));
    return resolvedColumnLayout
      .map(item => {
        const column = columnByKey.get(item.key);
        return column ? { ...column, visible: item.visible } : null;
      })
      .filter(Boolean);
  }, [columns, resolvedColumnLayout]);

  const visibleColumns = React.useMemo(() => (
    orderedColumns.filter(column => column.visible)
  ), [orderedColumns]);

  const tableMinWidth = React.useMemo(() => (
    48 + visibleColumns.reduce((total, column) => total + Number(column.width || 120), 0)
  ), [visibleColumns]);

  React.useEffect(() => {
    persistColumnLayout(resolvedColumnLayout);
  }, [resolvedColumnLayout]);

  const displayedArticles = React.useMemo(() => {
    const filtered = articles.filter(article => Object.entries(filters).every(([key, filterValue]) => {
      if (!filterValue) return true;
      const articleValue = getFilterValue(article, key);
      if (['media', 'tier', 'sentiment', 'exposure', 'editorialOrigin', 'spokesperson', 'keyMessage', 'kpia'].includes(key)) {
        return String(articleValue) === String(filterValue);
      }
      return normalizeText(articleValue).includes(normalizeText(filterValue));
    }));

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * direction;
      }

      return String(aValue).localeCompare(String(bValue), 'pt-BR', { sensitivity: 'base' }) * direction;
    });
  }, [articles, filters, sortConfig]);

  const selectedArticleIds = React.useMemo(() => (
    (selectedArticles || []).filter(articleId => articles.some(article => article._id === articleId))
  ), [articles, selectedArticles]);

  const allDisplayedSelected = displayedArticles.length > 0 && displayedArticles.every(article => selectedSet.has(article._id));
  const someDisplayedSelected = displayedArticles.some(article => selectedSet.has(article._id));

  const inspectorArticle = React.useMemo(() => {
    if (!inspectorArticleId) return null;
    return articles.find(article => article._id === inspectorArticleId) || null;
  }, [articles, inspectorArticleId]);

  const handleSort = (key) => {
    setSortConfig(previous => ({
      key,
      direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(previous => ({
      ...previous,
      [key]: value
    }));
  };

  const handleToggleColumn = (key) => {
    setColumnLayout(previous => {
      const nextLayout = normalizeColumnLayout(columns, previous);
      const visibleCount = nextLayout.filter(item => item.visible).length;

      return nextLayout.map(item => {
        if (item.key !== key) return item;
        if (item.visible && visibleCount <= 1) return item;
        return { ...item, visible: !item.visible };
      });
    });
  };

  const handleMoveColumn = (key, direction) => {
    setColumnLayout(previous => {
      const nextLayout = normalizeColumnLayout(columns, previous);
      const index = nextLayout.findIndex(item => item.key === key);
      const targetIndex = index + direction;

      if (index < 0 || targetIndex < 0 || targetIndex >= nextLayout.length) {
        return nextLayout;
      }

      const moved = [...nextLayout];
      const [item] = moved.splice(index, 1);
      moved.splice(targetIndex, 0, item);
      return moved;
    });
  };

  const handleResetColumnLayout = () => {
    setColumnLayout(normalizeColumnLayout(columns, null));
  };

  const handleExportVisibleExcel = () => {
    exportRowsToExcel(displayedArticles, visibleColumns);
  };
  const handleExportVisibleCsv = handleExportVisibleExcel;

  const handleInlineQualitativeChange = async (article, field, value) => {
    if (!article?._id || !field?.toPatch) return;
    if (!onMeasurementSave) {
      setBulkError('Handler de salvamento nao configurado.');
      return;
    }

    const savingKey = `${article._id}:${field.key}`;
    setInlineSaving(previous => ({ ...previous, [savingKey]: true }));
    setBulkError('');

    try {
      await onMeasurementSave(article._id, field.toPatch(value, article));
    } catch (error) {
      setBulkError(error.response?.data?.message || error.message || 'Erro ao atualizar KPI.');
    } finally {
      setInlineSaving(previous => {
        const next = { ...previous };
        delete next[savingKey];
        return next;
      });
    }
  };

  const handleBulkFieldChange = (editId, fieldKey) => {
    const nextField = qualitativeMeasurementFields.find(field => field.key === fieldKey) || qualitativeMeasurementFields[0];
    setBulkEdits(previous => previous.map(edit => (
      edit.id === editId
        ? { ...edit, field: nextField.key, value: nextField.defaultValue }
        : edit
    )));
    setBulkError('');
  };

  const handleBulkValueChange = (editId, value) => {
    setBulkEdits(previous => previous.map(edit => (
      edit.id === editId ? { ...edit, value } : edit
    )));
    setBulkError('');
  };

  const handleAddBulkEdit = () => {
    const usedFields = new Set(bulkEdits.map(edit => edit.field));
    const nextField = qualitativeMeasurementFields.find(field => !usedFields.has(field.key)) || qualitativeMeasurementFields[0];
    setBulkEdits(previous => [...previous, createBulkEdit(nextField.key)]);
    setBulkError('');
  };

  const handleRemoveBulkEdit = (editId) => {
    setBulkEdits(previous => {
      const next = previous.filter(edit => edit.id !== editId);
      return next.length > 0 ? next : [createBulkEdit()];
    });
    setBulkError('');
  };

  const buildBulkPatch = () => {
    const patch = {};

    bulkEdits.forEach((edit) => {
      const field = qualitativeMeasurementFields.find(item => item.key === edit.field);
      if (!field) return;
      if (edit.value === '' || edit.value === null || edit.value === undefined) return;
      Object.assign(patch, field.toPatch(edit.value));
    });

    return patch;
  };

  const handleApplyBulkEdits = async () => {
    if (selectedArticleIds.length === 0) {
      setBulkError('Selecione pelo menos um artigo.');
      return false;
    }

    const patch = buildBulkPatch();
    if (Object.keys(patch).length === 0) {
      setBulkError('Escolha ao menos um campo com valor.');
      return false;
    }

    setBulkSaving(true);
    setBulkError('');

    try {
      if (onMeasurementBulkSave) {
        await onMeasurementBulkSave(selectedArticleIds, patch);
      } else if (onMeasurementSave) {
        await Promise.all(selectedArticleIds.map(articleId => onMeasurementSave?.(articleId, patch)));
      } else {
        throw new Error('Handler de salvamento nao configurado.');
      }
      return true;
    } catch (error) {
      setBulkError(error.response?.data?.message || error.message || 'Erro ao aplicar nos selecionados.');
      return false;
    } finally {
      setBulkSaving(false);
    }
  };

  const handleHeaderSelection = (event) => {
    event.stopPropagation();
    if (allDisplayedSelected) {
      displayedArticles.forEach((article) => {
        if (selectedSet.has(article._id)) {
          onSelectArticle?.(article._id);
        }
      });
    } else {
      displayedArticles.forEach((article) => {
        if (!selectedSet.has(article._id)) {
          onSelectArticle?.(article._id);
        }
      });
    }
  };

  const handleRowClick = (article) => {
    if (selectionMode) {
      onSelectArticle?.(article._id);
      return;
    }
    setInspectorArticleId(article._id);
  };

  const renderArticleCell = (article, key) => {
    const isInlineDisabled = (fieldKey) => !onMeasurementSave || Boolean(inlineSaving[`${article._id}:${fieldKey}`]);

    switch (key) {
      case 'article':
        return <SheetCellText strong title={article.title}>{article.title}</SheetCellText>;
      case 'originalLink':
        return <OriginalLinkCell url={getOriginalUrl(article)} />;
      case 'date':
        return <SheetCellText>{getColumnExportValue(article, key)}</SheetCellText>;
      case 'source': {
        const siteName = getSiteName(article);
        return <SheetCellText title={siteName}>{siteName || 'Fonte desconhecida'}</SheetCellText>;
      }
      case 'author': {
        const author = getAuthorValue(article);
        return <SheetCellText title={author}>{author}</SheetCellText>;
      }
      case 'media':
        return <SheetCellText>{article.mediaType || '-'}</SheetCellText>;
      case 'tier':
      case 'sentiment':
      case 'exposure':
      case 'editorialOrigin':
      case 'spokesperson':
      case 'keyMessage':
        return (
          <QualitativeSelectCell
            article={article}
            fieldKey={key}
            disabled={isInlineDisabled(key)}
            onChange={handleInlineQualitativeChange}
          />
        );
      case 'editorial':
        return <SheetCellText title={getEditorialOrigin(article)}>{getEditorialOrigin(article)}</SheetCellText>;
      case 'valuation':
      case 'audience':
      case 'audienceDaily':
      case 'audienceMonthly':
      case 'centimetragem':
      case 'views':
      case 'likes':
      case 'comments':
      case 'shares':
        return <SheetCellText>{getColumnExportValue(article, key)}</SheetCellText>;
      case 'tags': {
        const tagsText = getTagsText(article);
        return <SheetCellText title={tagsText}>{tagsText}</SheetCellText>;
      }
      case 'smartScore':
        return <SheetCellText strong>{getColumnExportValue(article, key)}</SheetCellText>;
      case 'kpia':
        return <SheetCellText>{getKpiaStatus(article)}</SheetCellText>;
      default:
        return <SheetCellText>{getColumnExportValue(article, key)}</SheetCellText>;
    }
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: 0,
          overflow: 'hidden',
          border: 0,
          background: 'transparent',
          boxShadow: 'none'
        }}
      >
        <Box sx={{ px: 0.8, py: 0.55, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(15,23,42,0.07)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 560 }}>
            {displayedArticles.length} de {articles.length} artigos na tabela
          </Typography>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <Button
              size="small"
              variant="text"
              startIcon={<ColumnsIcon sx={{ fontSize: 15 }} />}
              onClick={() => setColumnDialogOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 1, fontSize: '0.72rem', minHeight: 24, px: 1 }}
            >
              Configurar planilha
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
              disabled={loading || visibleColumns.length === 0}
              onClick={handleExportVisibleExcel}
              sx={{ textTransform: 'none', borderRadius: 1, fontSize: '0.72rem', minHeight: 24, px: 1 }}
            >
              Exportar Excel
            </Button>
            <Button
              size="small"
              variant="text"
              disabled={!Object.values(filters).some(Boolean)}
              onClick={() => setFilters({})}
              sx={{ textTransform: 'none', borderRadius: 1, fontSize: '0.72rem', minHeight: 24, px: 1 }}
            >
              Limpar filtros
            </Button>
          </Stack>
        </Box>

        <MeasurementBulkBar
          selectedCount={selectedArticleIds.length}
          edits={bulkEdits}
          saving={bulkSaving}
          error={bulkError}
          onAddEdit={handleAddBulkEdit}
          onRemoveEdit={handleRemoveBulkEdit}
          onFieldChange={handleBulkFieldChange}
          onValueChange={handleBulkValueChange}
          onApply={handleApplyBulkEdits}
        />

        <Box
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            ...operationalScrollbars.thin
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              minWidth: tableMinWidth,
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              '& th, & td': {
                px: 0.8,
                py: 0.58,
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.055)' : 'rgba(15,23,42,0.07)'}`,
                textAlign: 'left',
                verticalAlign: 'middle'
              },
              '& thead th': {
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: isDarkMode ? '#202124' : '#f8fafc',
                py: 0.52,
                verticalAlign: 'top'
              },
              '& tbody td': {
                height: 34
              },
              '& tbody tr': {
                cursor: 'pointer'
              },
              '& tbody tr:hover td': {
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.026)' : 'rgba(15,23,42,0.025)'
              },
              '& tbody tr.is-selected td': {
                bgcolor: isDarkMode ? 'rgba(148,163,184,0.11)' : 'rgba(148,163,184,0.16)'
              }
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 48 }}>
                  <NativeCheckbox
                    checked={allDisplayedSelected}
                    indeterminate={!allDisplayedSelected && someDisplayedSelected}
                    onChange={handleHeaderSelection}
                    ariaLabel="Selecionar artigos filtrados"
                  />
                </th>
                {visibleColumns.map(column => (
                  <th key={column.key} style={{ width: column.width }}>
                    <Stack spacing={0.4}>
                      <ColumnSortButton
                        label={column.label}
                        active={sortConfig.key === column.key}
                        direction={sortConfig.direction}
                        onClick={() => handleSort(column.key)}
                      />
                      <ColumnFilter
                        type={column.filter}
                        value={filters[column.key] || ''}
                        options={column.options}
                        placeholder={column.placeholder}
                        onChange={(value) => handleFilterChange(column.key, value)}
                      />
                    </Stack>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, fontWeight: 650 }}>
                      Carregando dados da tabela...
                    </Typography>
                  </td>
                </tr>
              ) : displayedArticles.map((article) => {
                const isSelected = selectedSet.has(article._id);

                return (
                  <tr
                    key={article._id}
                    className={isSelected ? 'is-selected' : undefined}
                    onClick={() => handleRowClick(article)}
                  >
                    <td>
                      <NativeCheckbox
                        checked={isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onSelectArticle?.(article._id)}
                        ariaLabel={`Selecionar ${article.title}`}
                      />
                    </td>

                    {visibleColumns.map(column => (
                      <td key={`${article._id}-${column.key}`}>
                        {renderArticleCell(article, column.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      </Box>

      <ColumnLayoutDialog
        open={columnDialogOpen}
        columns={columns}
        layout={resolvedColumnLayout}
        onClose={() => setColumnDialogOpen(false)}
        onToggle={handleToggleColumn}
        onMove={handleMoveColumn}
        onReset={handleResetColumnLayout}
      />

      <MeasurementInspectorDrawer
        open={Boolean(inspectorArticle)}
        article={inspectorArticle}
        onClose={() => setInspectorArticleId(null)}
        onSave={onMeasurementSave}
        onArticleClick={onArticleClick}
        onEdit={onEdit}
      />
    </>
  );
};

ArticleMeasurementTable.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  selectedArticles: PropTypes.arrayOf(PropTypes.string),
  selectionMode: PropTypes.bool,
  onSelectArticle: PropTypes.func,
  onArticleClick: PropTypes.func,
  onEdit: PropTypes.func,
  onMeasurementSave: PropTypes.func,
  onMeasurementBulkSave: PropTypes.func
};

export default React.memo(ArticleMeasurementTable);
